import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'

export const COLOR_SWATCHES = Object.freeze(Array.from(new Set([
  '#FFFFFF', '#F1F3F4', '#DADCE0', '#BABCBE', '#8A8D91', '#5F6368', '#3C4043', '#202124', '#0B0B0B', '#000000',
  '#F23645', '#FF9800', '#FFEB3B', '#4CAF50', '#089981', '#00BCD4', '#2962FF', '#673AB7', '#9C27B0', '#E91E63',
  '#F8BBD0', '#FFE0B2', '#FFF9C4', '#C8E6C9', '#B2DFDB', '#B2EBF2', '#BBDEFB', '#D1C4E9', '#E1BEE7', '#F8BBD0',
  '#EF9A9A', '#FFCC80', '#FFF59D', '#A5D6A7', '#80CBC4', '#80DEEA', '#90CAF9', '#B39DDB', '#CE93D8', '#F48FB1',
  '#E57373', '#FFB74D', '#FFF176', '#81C784', '#4DB6AC', '#4DD0E1', '#64B5F6', '#9575CD', '#BA68C8', '#F06292',
  '#FF5252', '#FF9800', '#FFEB3B', '#66BB6A', '#26A69A', '#26C6DA', '#2196F3', '#7E57C2', '#AB47BC', '#EC407A',
  '#B71C1C', '#E65100', '#F57F17', '#1B5E20', '#004D40', '#006064', '#0D47A1', '#311B92', '#4A148C', '#880E4F',
  '#8E1B1B', '#E64A19', '#F57C00', '#146C2E', '#00574B', '#007276', '#1E3A8A', '#4527A0', '#6A1B9A', '#AD1457',
])))

const HEX_RE = /^#[0-9a-fA-F]{6}$/
const PALETTE_WIDTH = 220
const PALETTE_HEIGHT_ESTIMATE = 240
const VIEWPORT_GUTTER = 8

function clampOpacity(value, fallback = 100) {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  return Math.max(0, Math.min(100, Math.round(n)))
}

export default function ColorOpacityControl({
  color,
  opacity = 100,
  onColorChange,
  onOpacityChange,
  triggerTestId,
  swatchTestIdPrefix,
  hexInputTestId,
  opacityTestId,
  ariaLabel,
  paletteClassName = '',
}) {
  const rootRef = useRef(null)
  const paletteRef = useRef(null)
  const triggerRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [hexDraft, setHexDraft] = useState(color || '')
  const [palettePosition, setPalettePosition] = useState({ top: 0, left: 0 })
  const safeColor = HEX_RE.test(color || '') ? color : '#FFFFFF'
  const safeOpacity = clampOpacity(opacity)

  useEffect(() => {
    setHexDraft(color || '')
  }, [color])

  useEffect(() => {
    if (!open) return
    function updatePalettePosition() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const viewportWidth = window.innerWidth || 0
      const viewportHeight = window.innerHeight || 0
      const paletteHeight = paletteRef.current?.offsetHeight || PALETTE_HEIGHT_ESTIMATE
      const left = Math.max(
        VIEWPORT_GUTTER,
        Math.min(rect.left, viewportWidth - PALETTE_WIDTH - VIEWPORT_GUTTER)
      )
      const belowTop = rect.bottom + VIEWPORT_GUTTER
      const aboveTop = rect.top - paletteHeight - VIEWPORT_GUTTER
      const maxTop = viewportHeight - paletteHeight - VIEWPORT_GUTTER
      const top = belowTop + paletteHeight <= viewportHeight - VIEWPORT_GUTTER
        ? belowTop
        : Math.max(VIEWPORT_GUTTER, aboveTop >= VIEWPORT_GUTTER ? aboveTop : maxTop)
      setPalettePosition({ top, left })
    }
    function onMouseDown(e) {
      const inTrigger = rootRef.current && rootRef.current.contains(e.target)
      const inPalette = paletteRef.current && paletteRef.current.contains(e.target)
      if (!inTrigger && !inPalette) setOpen(false)
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    updatePalettePosition()
    window.addEventListener('mousedown', onMouseDown)
    window.addEventListener('resize', updatePalettePosition)
    window.addEventListener('scroll', updatePalettePosition, true)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('resize', updatePalettePosition)
      window.removeEventListener('scroll', updatePalettePosition, true)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  function handleHexChange(e) {
    const v = e.target.value
    setHexDraft(v)
    if (HEX_RE.test(v)) onColorChange?.(v)
  }

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        data-testid={triggerTestId}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="h-8 w-10 rounded-md border border-slate-300 bg-white p-1 shadow-sm transition-colors hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-[#2962FF] dark:border-white/20 dark:bg-white/[0.03] dark:hover:border-white/40"
      >
        <span
          className="block h-full w-full rounded-sm"
          style={{ backgroundColor: safeColor }}
          aria-hidden
        />
      </button>
      {open && createPortal(
        <div
          ref={paletteRef}
          role="dialog"
          data-color-opacity-popover="true"
          data-testid={`${triggerTestId}-palette`}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className={`fixed z-[1000] w-[220px] rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-900 shadow-xl shadow-slate-900/15 dark:border-white/10 dark:bg-[#1f1f1f] dark:text-white ${paletteClassName}`}
          style={{ top: palettePosition.top, left: palettePosition.left }}
        >
          <div className="grid grid-cols-10 gap-1">
            {COLOR_SWATCHES.map((hex, idx) => (
              <button
                key={`${hex}-${idx}`}
                type="button"
                data-testid={`${swatchTestIdPrefix}-${hex}`}
                aria-label={`color ${hex}`}
                onClick={() => onColorChange?.(hex)}
                className={`h-4 w-4 rounded border transition-colors ${
                  color === hex ? 'border-slate-700 ring-2 ring-[#2962FF] dark:border-white' : 'border-slate-200 hover:border-slate-500 dark:border-white/10 dark:hover:border-white/50'
                }`}
                style={{ backgroundColor: hex }}
              />
            ))}
          </div>
          <div className="mt-2 border-t border-slate-200 pt-2 dark:border-white/10">
            <div className="flex items-center gap-2">
              <input
                data-testid={hexInputTestId}
                type="text"
                value={hexDraft}
                placeholder="#RRGGBB"
                onChange={handleHexChange}
                className="h-6 w-20 rounded bg-white border border-slate-300 px-2 font-mono text-[11px] text-slate-900 outline-none focus:border-slate-500 dark:bg-white/[0.04] dark:border-white/10 dark:text-white dark:focus:border-white/30"
              />
              <div className="h-px w-4 bg-slate-500 dark:bg-white/70" />
              <div className="h-4 w-4 rounded-sm border border-slate-200 dark:border-white/10" style={{ backgroundColor: safeColor }} />
            </div>
            {onOpacityChange && (
              <div className="mt-1.5">
                <label className="block text-slate-500 mb-1 dark:text-white/50">Opacity</label>
                <div className="flex items-center gap-2">
                  <input
                    data-testid={opacityTestId}
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={safeOpacity}
                    onChange={(e) => onOpacityChange(clampOpacity(e.target.value))}
                    className="min-w-0 flex-1 accent-[#089981]"
                  />
                  <div className="w-12 rounded border border-slate-200 bg-slate-50 px-1.5 py-1 text-right font-mono text-xs dark:border-white/15 dark:bg-white/[0.04]">
                    {safeOpacity}%
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      , document.body)}
    </div>
  )
}

ColorOpacityControl.propTypes = {
  color: PropTypes.string,
  opacity: PropTypes.number,
  onColorChange: PropTypes.func,
  onOpacityChange: PropTypes.func,
  triggerTestId: PropTypes.string.isRequired,
  swatchTestIdPrefix: PropTypes.string.isRequired,
  hexInputTestId: PropTypes.string.isRequired,
  opacityTestId: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  paletteClassName: PropTypes.string,
}
